import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';

import { isTouchOnlyDevice } from 'helpers/ClientUtils';

interface Options {
  enabled: boolean;
}

/**
 * Track whether the cursor is over the card. We hit-test cursor position rather
 * than use `mouseenter`/`mouseleave`, which can drop the leave (tab switch,
 * minimize, fast scroll). Inert unless `enabled` and never armed on touch-only
 * devices; an `IntersectionObserver` limits the work to visible cards.
 * Shopbop Code Reference: https://code.amazon.com/packages/ShopbopSharedReactComponents/blobs/mainline/--/packages/shared-react-components/src/Molecules/ProductTile/useIsMouseOver.tsx
 */
export const useIsMouseOver = <T extends HTMLElement | null>(ref: MutableRefObject<T>, { enabled }: Options): boolean => {
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!enabled || isTouchOnlyDevice() || !ref.current) {
      setIsHovered(false);
      return undefined;
    }

    const HOVER_OUT_GRACE_MS = 120;
    let x = 0;
    let y = 0;
    let frame = 0;
    let intersecting = false;
    let outTimer = 0;
    let hasPointer = false;

    const commitInside = () => {
      if (outTimer) {
        clearTimeout(outTimer);
        outTimer = 0;
      }
      setIsHovered(true);
    };

    const commitOutsideSoon = () => {
      if (outTimer) {
        return;
      }
      outTimer = window.setTimeout(() => {
        outTimer = 0;
        setIsHovered(false);
      }, HOVER_OUT_GRACE_MS);
    };

    const scheduleHitTest = () => {
      cancelAnimationFrame(frame);
      if (!intersecting || !hasPointer) {
        commitOutsideSoon();
        return;
      }
      frame = requestAnimationFrame(() => {
        if (!ref.current || !intersecting) {
          commitOutsideSoon();
          return;
        }
        const rect = ref.current.getBoundingClientRect();
        const inside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        if (inside) {
          commitInside();
        } else {
          commitOutsideSoon();
        }
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      x = e.clientX;
      y = e.clientY;
      hasPointer = true;
      scheduleHitTest();
    };
    const handleScroll = () => {
      scheduleHitTest();
    };

    /**
     * Resets hover when the page loses pointer context.
     * Hover is derived from `mousemove`/`scroll`, but those stop firing when the window loses focus (Cmd/Alt+Tab) or the tab is hidden
     * freezing a `true` hover and leaving the rotation running in the background.
     * Clearing `hasPointer` and scheduling a hover-out resets the card to MAIN;
     * A fresh `mousemove` re-arms it on return (and prevents a stale scroll from doing so).
     */
    const resetHover = () => {
      hasPointer = false;
      commitOutsideSoon();
    };

    /**
     * Reset hover when the window loses focus (Cmd/Alt+Tab to another app).
     * The page stays "visible" in this case, so `visibilitychange` never fires —
     * `blur` is the only signal that the cursor context is gone.
     */
    const handleWindowBlur = () => resetHover();

    /**
     * Reset hover when the tab is backgrounded or the window is minimized.
     * `visibilitychange` fires on both hide and show, so we act only on the hide
     * transition (`document.hidden`) and ignore the return, where hover is already off.
     */
    const handleVisibilityChange = () => {
      if (document.hidden) {
        resetHover();
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        intersecting = entry.isIntersecting;
      });
    });
    io.observe(ref.current);

    return () => {
      io.disconnect();
      cancelAnimationFrame(frame);
      if (outTimer) {
        clearTimeout(outTimer);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll, { capture: true } as EventListenerOptions);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [ref, enabled]);

  return isHovered;
};
